import { useHealth } from '@/lib/hooks/useHealth';
import { Card, CardHeader } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';

/**
 * Calls the real GET /api/health endpoint and renders exactly what comes
 * back — no fabricated statistics. This clearly distinguishes
 * "website/API is online" from "bot bridge is configured/reachable": the
 * Bot Bridge now exists (bridge/server.py, see dembot/docs/BOT_BRIDGE.md)
 * and `reachable` reflects a real signed health check against it, not a
 * guess from whether it's merely configured.
 */
export function HealthStatusCard() {
  const health = useHealth();

  return (
    <Card>
      <CardHeader
        title="System status"
        description="Live from the Dem Web API — nothing on this card is simulated."
      />

      {health.status === 'loading' ? (
        <div className="space-y-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-5 w-56" />
        </div>
      ) : null}

      {health.status === 'error' ? (
        <Alert
          tone="danger"
          title="Website API unreachable"
          description={
            health.error.code === 'NETWORK_ERROR'
              ? 'Could not reach the Worker. If you are running this locally, make sure `npm run dev:worker` is running and VITE_API_BASE_URL points to it.'
              : health.error.message
          }
        />
      ) : null}

      {health.status === 'success' ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge tone="analytics">Website / API: online</Badge>
            <span className="text-small text-text-tertiary">
              {health.data.environment} · {new Date(health.data.timestamp).toLocaleString()}
            </span>
          </div>

          <Alert
            tone={health.data.botBridge.configured ? 'success' : 'warning'}
            title={
              health.data.botBridge.configured
                ? 'Bot Bridge: configured'
                : 'Bot Bridge: not configured'
            }
            description={health.data.botBridge.note}
          />
        </div>
      ) : null}
    </Card>
  );
}
